/**
 * Resource Alerts — Zod schemas for all 9 endpoints.
 * Base: /organizations/{org_id}/alerts/resource  ·  module gate: sysmon.
 *
 * `.nullish()` on every field the server may send as null/omit, so a null
 * never blanks the UI (the schema layer's whole job).
 */
import { z } from "zod";

export const alertTypeSchema = z.enum([
  "cpu", "memory", "disk_io", "zombie_processes", "load_average",
]);
export type AlertType = z.infer<typeof alertTypeSchema>;

export const severitySchema = z.enum(["critical", "high", "medium", "low", "info"]);
export type Severity = z.infer<typeof severitySchema>;

export const alertStateSchema = z.enum(["open", "acknowledged", "resolved"]);
export type AlertState = z.infer<typeof alertStateSchema>;

/* ── shared nested shapes ── */
const metricSchema = z.object({
  value:           z.number().nullish(),
  threshold:       z.number().nullish(),
  clear_threshold: z.number().nullish(),
  unit:            z.string().nullish(),
});

/** {id,name,email} — same shape for acknowledged_by and updated_by. */
const actorSchema = z.union([
  z.object({
    id:    z.string().nullish(),
    name:  z.string().nullish(),
    email: z.string().nullish(),
  }),
  z.string(), // some payloads send a bare name string
]).nullish();
export type Actor = z.infer<typeof actorSchema>;

/* ── EP1/EP2 · alert row (detail adds `event`) ── */
export const alertRowSchema = z.object({
  alert_id:        z.string(),
  alert_type:      alertTypeSchema,
  kind:            z.string().nullish(),
  agent_id:        z.string().nullish(),
  host:            z.string().nullish(),
  severity:        severitySchema,
  title:           z.string(),
  message:         z.string().nullish(),
  state:           alertStateSchema,
  acknowledged:    z.boolean(),
  acknowledged_at: z.string().nullish(),
  acknowledged_by: actorSchema,
  first_ts:        z.string().nullish(),
  last_ts:         z.string().nullish(),
  count:           z.number().nullish(),
  trigger_value:   z.number().nullish(),
  peak_value:      z.number().nullish(),
  last_value:      z.number().nullish(),
  metric:          metricSchema.nullish(),
});
export type AlertRow = z.infer<typeof alertRowSchema>;

export const alertDetailSchema = alertRowSchema.extend({
  event: z.object({
    host:    z.string().nullish(),
    type:    z.string().nullish(),
    payload: z.record(z.string(), z.unknown()).nullish(),
  }).nullish(),
});
export type AlertDetail = z.infer<typeof alertDetailSchema>;

export const alertListSchema = z.object({
  org_id:   z.string().nullish(),
  items:    z.array(alertRowSchema),
  total:    z.number(),
  page:     z.number(),
  per_page: z.number(),
  pages:    z.number(),
});
export type AlertList = z.infer<typeof alertListSchema>;

/* ── EP5 · summary (bell badge) ── */
const typeSplitSchema = z.object({
  active:         z.number(),
  unacknowledged: z.number(),
});
export const alertSummarySchema = z.object({
  org_id:         z.string().nullish(),
  active:         z.number(),
  unacknowledged: z.number(),
  acknowledged:   z.number(),
  by_type:        z.record(z.string(), typeSplitSchema).nullish(),
});
export type AlertSummary = z.infer<typeof alertSummarySchema>;

/* ── EP3 · single acknowledge ── */
export const ackOneSchema = z.object({
  was_first:                z.boolean(),
  alert_id:                 z.string(),
  acknowledged_at:          z.string().nullish(),
  acknowledged_by:          z.string().nullish(),
  already_acknowledged_by:  z.string().nullish(),
  already_acknowledged_at:  z.string().nullish(),
});
export type AckOne = z.infer<typeof ackOneSchema>;

/* ── EP4 · bulk acknowledge ── */
export const ackBulkSchema = z.object({
  org_id:               z.string().nullish(),
  acknowledged:         z.array(z.string()),
  already_acknowledged: z.array(z.string()),
  not_found:            z.array(z.string()),
  total_requested:      z.number(),
});
export type AckBulk = z.infer<typeof ackBulkSchema>;

/* ── EP6-9 · config ── */
export const ruleSchema = z.object({
  enabled:             z.boolean(),
  severity:            severitySchema,
  threshold:           z.number(),
  clear_threshold:     z.number(),
  for_seconds:         z.number().nullish(),
  occurrences:         z.number().nullish(),
  clear_after_seconds: z.number().nullish(),
  cooldown_seconds:    z.number().nullish(),
  options:             z.record(z.string(), z.unknown()).nullish(),
});
export type Rule = z.infer<typeof ruleSchema>;

const catalogOptionSchema = z.object({
  key:     z.string(),
  type:    z.enum(["enum", "bool", "number", "string"]),
  choices: z.array(z.string()).nullish(),
  default: z.unknown().nullish(),
});
export const catalogEntrySchema = z.object({
  key:          alertTypeSchema,
  label:        z.string(),
  description:  z.string().nullish(),
  unit:         z.string().nullish(),
  decimals:     z.number().nullish(),
  comparator:   z.string().nullish(),
  min:          z.number().nullish(),
  max:          z.number().nullish(),
  source_event: z.string().nullish(),
  options:      z.array(catalogOptionSchema).nullish(),
  defaults:     ruleSchema,
});
export type CatalogEntry = z.infer<typeof catalogEntrySchema>;
export type CatalogOption = z.infer<typeof catalogOptionSchema>;

export const configSchema = z.object({
  org_id:              z.string().nullish(),
  catalog:             z.array(catalogEntrySchema),
  rules:               z.record(z.string(), ruleSchema),
  version:             z.number(),
  updated_at:          z.string().nullish(),
  updated_by:          actorSchema,
  is_default:          z.boolean(),
  propagation_seconds: z.number().nullish(),
  enabled_count:       z.number().nullish(),
});
export type Config = z.infer<typeof configSchema>;

export const saveConfigSchema = z.object({
  org_id:              z.string().nullish(),
  rules:               z.record(z.string(), ruleSchema),
  version:             z.number(),
  updated_at:          z.string().nullish(),
  updated_by:          actorSchema,
  changed_types:       z.array(z.string()).nullish(),
  is_default:          z.boolean().nullish(),
  propagation_seconds: z.number().nullish(),
});
export type SaveConfig = z.infer<typeof saveConfigSchema>;

export const toggleConfigSchema = z.object({
  org_id:     z.string().nullish(),
  alert_type: alertTypeSchema,
  enabled:    z.boolean(),
  rule:       ruleSchema,
  version:    z.number(),
});
export type ToggleConfig = z.infer<typeof toggleConfigSchema>;

export const resetConfigSchema = z.object({
  org_id:     z.string().nullish(),
  rules:      z.record(z.string(), ruleSchema),
  version:    z.number(),
  updated_at: z.string().nullish(),
  reset:      z.boolean(),
});
export type ResetConfig = z.infer<typeof resetConfigSchema>;

/* ── query shape for the list ── */
export interface AlertsQuery {
  state?:        AlertState[];
  alert_type?:   AlertType[];
  severity?:     Severity[];
  agent_id?:     string;
  acknowledged?: boolean;
  search?:       string;
  page?:         number;
  per_page?:     number;
}
